import React, { useState, useRef, useEffect } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sparkles, Send } from "lucide-react";

export default function AIAssistant() {
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hi — I'm TrueLine AI. Ask me about ADAS procedures, OEM requirements, or justification language for any claim." },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(`session-${Date.now()}`);
  const endRef = useRef(null);

  useEffect(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), [messages]);

  const send = async () => {
    if (!input.trim() || loading) return;
    const userMsg = { role: "user", content: input };
    setMessages((m) => [...m, userMsg]);
    setInput("");
    setLoading(true);
    try {
      const res = await api.post("/ai/chat", { message: userMsg.content, session_id: sessionId });
      setMessages((m) => [...m, { role: "assistant", content: res.data.reply }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: "Sorry — AI service unavailable. Please try again." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4 animate-slide-up max-w-4xl flex flex-col h-[calc(100vh-8rem)]" data-testid="ai-page">
      <div>
        <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-bold mb-2 flex items-center gap-2"><Sparkles className="w-3.5 h-3.5 text-cyan-400" />GPT-4o · Powered by Emergent</div>
        <h1 className="font-display text-4xl font-semibold tracking-tight">TrueLine AI Assistant</h1>
      </div>

      <div className="flex-1 rounded-xl border border-slate-800 bg-[#0B1120] p-5 overflow-y-auto space-y-3">
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
              m.role === "user" ? "bg-blue-600 text-white" : "bg-slate-900/60 border border-slate-800 text-slate-200"
            }`} data-testid={`msg-${i}`}>
              {m.content}
            </div>
          </div>
        ))}
        {loading && <div className="flex justify-start"><div className="bg-slate-900/60 border border-slate-800 rounded-2xl px-4 py-3 text-sm text-cyan-400">Thinking...</div></div>}
        <div ref={endRef} />
      </div>

      <div className="flex gap-2">
        <Input data-testid="ai-input" placeholder="Ask about ADAS, OEM, or claims..." value={input} onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()} className="bg-[#0B1120] border-slate-800 h-11" />
        <Button data-testid="ai-send-btn" onClick={send} disabled={loading} className="bg-blue-600 hover:bg-blue-500 h-11"><Send className="w-4 h-4" /></Button>
      </div>
    </div>
  );
}
